import { useEnvApi } from '../composables/useEnvApi'
import { useServerFetch } from '../composables/useServerFetch'
import { WIKIBITWEBV1 } from '../utils/constants'
import { request } from './request'

/** 软件鉴定标签。接口在不同版本中可能使用不同的扩展字段，因此保留未知字段。 */
export type ExchangeSoftwareIdentificationTag = {
  tagName?: string
  tagColor?: string
  [key: string]: unknown
}

/** MT4/MT5 牌照鉴定结果。 */
export type ExchangeSoftwareLicense = {
  status?: number | string
  icon?: string
  content?: string
  color?: string
  [key: string]: unknown
}

/** 软件列表项，兼容软件 2.0/3.0 两套接口字段。 */
export type ExchangeSoftwareItem = {
  softwareId?: string
  softwareType?: number | string
  showPlatform?: number | string
  appBundleId?: string
  marketId?: number | string
  name?: string
  title?: string
  logo?: string
  downloadNum?: number | string
  rankNo?: number | string
  isShowRank?: boolean
  sizes?: string
  companyId?: string
  companyIcon?: string
  developerName?: string
  screenshots?: string[]
  identificationResult?: ExchangeSoftwareIdentificationTag[]
  systemTypeImg?: string[]
  [key: string]: unknown
}

/** 服务器杠杆分级明细。 */
export type ExchangeServerLeverageDetail = {
  content?: string
  reminder?: string
  leverage?: string | number
  leverageLevel?: number | string
  [key: string]: unknown
}

/** 服务器 IP 明细。 */
export type ExchangeServerIpItem = {
  ip?: string
  pingText?: string
  velocity?: number | string
  countryName?: string
  flag?: string
  [key: string]: unknown
}

/** 服务器 IP 列表及更新时间。 */
export type ExchangeServerIpList = {
  title?: string
  updateDate?: string | number
  list: ExchangeServerIpItem[]
  [key: string]: unknown
}

/** MT4/MT5 服务器卡片数据。 */
export type ExchangeServerItem = {
  serverName?: string
  serverTypeText?: string
  serverTypeColor?: string
  ipCount?: number | string
  regulationCountryName?: string
  regulationCountrygFlag?: string
  regulationCountryFlag?: string
  leverage?: string
  leverageDetailList?: ExchangeServerLeverageDetail[]
  fastestServerIp?: string
  fastestServerPingText?: string
  fastestServerVelocity?: number | string
  serverIPList?: ExchangeServerIpList
  companyBasic?: Record<string, unknown> | null
  [key: string]: unknown
}

/** MT4/MT5 鉴定接口返回的数据。 */
export type ExchangeSoftwareAppraise = {
  identificationResult?: {
    licenses: ExchangeSoftwareLicense[]
    mT4_Count?: number | string
    mT5_Count?: number | string
    averageVelocityText?: string | number
    velocityIcon?: string
    [key: string]: unknown
  }
  serverList?: {
    allServers: ExchangeServerItem[]
    mT4_Servers: ExchangeServerItem[]
    mT5_Servers: ExchangeServerItem[]
    [key: string]: unknown
  }
  [key: string]: unknown
}

/** 交易所详情页软件模块使用的稳定数据结构。 */
export type ExchangeSoftwarePayload = {
  mt4mt5SoftwareList: ExchangeSoftwareItem[]
  selfSoftwareList: ExchangeSoftwareItem[]
  iosSelfSoftwareList: ExchangeSoftwareItem[]
  androidSelfSoftwareList: ExchangeSoftwareItem[]
  ctraderSoftwareList: ExchangeSoftwareItem[]
  iosCtraderSoftwareList: ExchangeSoftwareItem[]
  androidCtraderSoftwareList: ExchangeSoftwareItem[]
  mt4mt5AppraiseData: ExchangeSoftwareAppraise | null
}

/** 软件接口公共参数。接口会同时返回 all/iOS/Android 三个 tab。 */
export type ExchangeSoftwareRequestOptions = {
  traderCode?: string
  code?: string
  brandType?: number | string
  isContainerOldApp?: boolean
  deviceType?: number
  languageCode?: string
  countryCode?: string
  userId?: string | number
  [key: string]: unknown
}

/** 返回全新对象，避免多个页面实例共享可变数组。 */
export const emptyExchangeSoftwarePayload = (): ExchangeSoftwarePayload => ({
  mt4mt5SoftwareList: [],
  selfSoftwareList: [],
  iosSelfSoftwareList: [],
  androidSelfSoftwareList: [],
  ctraderSoftwareList: [],
  iosCtraderSoftwareList: [],
  androidCtraderSoftwareList: [],
  mt4mt5AppraiseData: null,
})

const SELF_SOFTWARE_PATH = '/api/v2/softwate/get_self_developed_software_list'
const CTRADER_SOFTWARE_PATH = '/api/v2/softwate/get_ctrader_software_list'
const SOFTWARE_AGGREGATE_ROUTE =
  'wikifxagg$trading_software_list,wikifxagg$self_developed_software_list,wikifxagg$company_ctrader_software_list,wikifxagg$mt4mt5_trading_software_list_web'
const MT4_MT5_APPRAISE_ROUTE =
  'wikifxagg$mt4_mt5_appraise_v2,wikifxagg$trader_fakepopup,wikifxagg$family_tree_entry_setting,wikifxagg$company_all_related_by_trader'

const DEFAULT_REQUEST_OPTIONS: Required<
  Pick<ExchangeSoftwareRequestOptions, 'brandType' | 'isContainerOldApp' | 'deviceType'>
> = {
  brandType: 1,
  isContainerOldApp: false,
  deviceType: 1,
}

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

const toText = (value: unknown): string => {
  return typeof value === 'string' || typeof value === 'number' ? String(value) : ''
}

const toMetric = (value: unknown): number | string | undefined => {
  return typeof value === 'string' || typeof value === 'number' ? value : undefined
}

const toBoolean = (value: unknown): boolean => {
  return value === true || value === 1 || value === '1' || value === 'true'
}

/** 接口偶尔将数组序列化为 JSON 字符串，统一恢复为数组。 */
const parseArray = (value: unknown): unknown[] => {
  if (Array.isArray(value)) return value
  if (typeof value !== 'string') return []

  const text = value.trim()
  if (!text) return []

  try {
    const parsed: unknown = JSON.parse(text)
    if (Array.isArray(parsed)) return parsed
  } catch {
    // 非 JSON 的逗号分隔图片地址继续按文本处理。
  }

  return text
    .split(/[,|\n]/)
    .map((item) => item.trim())
    .filter(Boolean)
}

/** 兼容网关 data/Data 多层包装；业务对象本身没有包装标记时保持原样。 */
const unwrapResponse = (response: unknown): unknown => {
  let payload = response

  for (let depth = 0; depth < 8; depth += 1) {
    if (!isRecord(payload)) return payload

    if (payload.succeed === false || payload.success === false) return null

    const hasEnvelopeMarker =
      'code' in payload || 'message' in payload || 'msg' in payload || 'success' in payload || 'succeed' in payload
    const isSingleDataWrapper = Object.keys(payload).length === 1

    // 聚合模块常见 `{ data: ... }` 形状没有 code/succeed 标记，单字段对象也需要拆开。
    if ((hasEnvelopeMarker || isSingleDataWrapper) && 'data' in payload) {
      payload = payload.data
      continue
    }

    if ((hasEnvelopeMarker || isSingleDataWrapper) && 'Data' in payload) {
      payload = payload.Data
      continue
    }

    return payload
  }

  return payload
}

/** 深度查找聚合响应中的指定字段，兼容模块名和数组索引变化。 */
const findFirstByKey = (value: unknown, key: string, depth = 0): unknown => {
  if (depth > 10 || value === null || value === undefined) return undefined
  if (Array.isArray(value)) {
    for (const child of value) {
      const found = findFirstByKey(child, key, depth + 1)
      if (found !== undefined) return found
    }
    return undefined
  }
  if (!isRecord(value)) return undefined

  if (Object.prototype.hasOwnProperty.call(value, key)) return value[key]

  for (const child of Object.values(value)) {
    const found = findFirstByKey(child, key, depth + 1)
    if (found !== undefined) return found
  }

  return undefined
}

/** 从 result/tabType 结构中提取 all、iOS、Android 三组软件。 */
const normalizeSoftwareTabs = (response: unknown) => {
  const unwrapped = unwrapResponse(response)
  const result = Array.isArray(unwrapped)
    ? unwrapped
    : parseArray(findFirstByKey(unwrapped, 'result') ?? findFirstByKey(unwrapped, 'items'))

  const all: ExchangeSoftwareItem[] = []
  const ios: ExchangeSoftwareItem[] = []
  const android: ExchangeSoftwareItem[] = []
  let hasTabItems = false

  for (const entry of result) {
    if (isRecord(entry) && ('items' in entry || 'tabType' in entry)) {
      hasTabItems = true
      const items = normalizeSoftwareItems(entry.items)
      const tabType = Number(entry.tabType)
      if (tabType === 1) ios.push(...items)
      else if (tabType === 2) android.push(...items)
      else all.push(...items)
      continue
    }

    const item = normalizeSoftwareItem(entry)
    if (item) all.push(item)
  }

  if (!hasTabItems && all.length === 0) {
    const directItems = normalizeSoftwareItems(unwrapped)
    all.push(...directItems)
  }

  const normalizedAll = dedupeSoftwareItems(all)
  const normalizedIos = dedupeSoftwareItems(ios)
  const normalizedAndroid = dedupeSoftwareItems(android)

  // 部分旧版本不返回 tabType=0，此时由两个平台列表合并出 All。
  if (normalizedAll.length === 0) {
    normalizedAll.push(...dedupeSoftwareItems([...normalizedIos, ...normalizedAndroid]))
  }

  return {
    all: normalizedAll,
    ios: normalizedIos,
    android: normalizedAndroid,
  }
}

/** 将图片、系统图标和鉴定标签字段归一为数组。 */
const normalizeStringArray = (value: unknown): string[] => {
  return parseArray(value)
    .map((item) => toText(item))
    .filter(Boolean)
}

const normalizeIdentificationTags = (value: unknown): ExchangeSoftwareIdentificationTag[] => {
  return parseArray(value)
    .filter(isRecord)
    .map((item) => ({
      ...item,
      tagName: toText(item.tagName ?? item.name ?? item.content ?? item.text),
      tagColor: toText(item.tagColor ?? item.color ?? item.fontColor),
    }))
    .filter((item) => item.tagName || item.tagColor)
}

/** 统一软件项字段，同时保留接口新增字段供展示层按需使用。 */
const normalizeSoftwareItem = (value: unknown): ExchangeSoftwareItem | null => {
  if (!isRecord(value)) return null

  const item: ExchangeSoftwareItem = {
    ...value,
    softwareId: toText(value.softwareId ?? value.id),
    softwareType: toMetric(value.softwareType),
    showPlatform: toMetric(value.showPlatform),
    appBundleId: toText(value.appBundleId ?? value.bundleId),
    marketId: toMetric(value.marketId),
    name: toText(value.name ?? value.appName),
    title: toText(value.title ?? value.subTitle),
    logo: toText(value.logo ?? value.icon),
    downloadNum: toMetric(value.downloadNum),
    rankNo: toMetric(value.rankNo),
    isShowRank: toBoolean(value.isShowRank),
    sizes: toText(value.sizes ?? value.size),
    companyId: toText(value.companyId),
    companyIcon: toText(value.companyIcon),
    developerName: toText(value.developerName ?? value.companyName),
    screenshots: normalizeStringArray(value.screenshots ?? value.screenShots ?? value.images),
    identificationResult: normalizeIdentificationTags(value.identificationResult ?? value.identificationTags),
    systemTypeImg: normalizeStringArray(value.systemTypeImg ?? value.systemTypeImages),
  }

  if (!item.softwareId && !item.name && !item.appBundleId && !item.logo) return null
  return item
}

const normalizeSoftwareItems = (value: unknown): ExchangeSoftwareItem[] => {
  return parseArray(value)
    .map(normalizeSoftwareItem)
    .filter((item): item is ExchangeSoftwareItem => Boolean(item))
}

/** 软件接口没有稳定主键时用组合字段去重，避免 All 与平台列表重复。 */
const dedupeSoftwareItems = (items: ExchangeSoftwareItem[]): ExchangeSoftwareItem[] => {
  const seen = new Set<string>()
  return items.filter((item) => {
    const key =
      item.softwareId || `${item.appBundleId || ''}:${item.marketId || ''}:${item.name || ''}:${item.logo || ''}`
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

const normalizeLicense = (value: unknown): ExchangeSoftwareLicense | null => {
  if (!isRecord(value)) return null
  return {
    ...value,
    status: toMetric(value.status),
    icon: toText(value.icon ?? value.iconUrl),
    content: toText(value.content ?? value.name ?? value.text),
    color: toText(value.color ?? value.fontColor),
  }
}

const normalizeLeverageDetail = (value: unknown): ExchangeServerLeverageDetail | null => {
  if (!isRecord(value)) return null
  return {
    ...value,
    content: toText(value.content ?? value.name ?? value.text),
    reminder: toText(value.reminder ?? value.description),
    leverage: toMetric(value.leverage),
    leverageLevel: toMetric(value.leverageLevel ?? value.level),
  }
}

const normalizeServerIp = (value: unknown): ExchangeServerIpItem | null => {
  if (!isRecord(value)) return null
  return {
    ...value,
    ip: toText(value.ip ?? value.serverIp ?? value.serverIP),
    pingText: toText(value.pingText ?? value.ping),
    velocity: toMetric(value.velocity),
    countryName: toText(value.countryName ?? value.regulationCountryName),
    flag: toText(value.flag ?? value.countryFlag),
  }
}

const normalizeServerIpList = (value: unknown): ExchangeServerIpList => {
  const source = isRecord(value) ? value : {}
  const listValue = source.list ?? source.items ?? source.ipList ?? source.ips ?? (Array.isArray(value) ? value : [])
  return {
    ...source,
    title: toText(source.title ?? source.name),
    updateDate: toMetric(source.updateDate ?? source.updateTime),
    list: parseArray(listValue)
      .map(normalizeServerIp)
      .filter((item): item is ExchangeServerIpItem => Boolean(item)),
  }
}

/** 统一服务器卡片字段并补齐 IP/杠杆数组。 */
const normalizeServer = (value: unknown): ExchangeServerItem | null => {
  if (!isRecord(value)) return null

  const ipList = normalizeServerIpList(value.serverIPList ?? value.serverIpList ?? value.ipList)
  const leverageDetailList = parseArray(value.leverageDetailList ?? value.leverageDetails)
    .map(normalizeLeverageDetail)
    .filter((item): item is ExchangeServerLeverageDetail => Boolean(item))

  return {
    ...value,
    serverName: toText(value.serverName ?? value.name),
    serverTypeText: toText(value.serverTypeText ?? value.typeText),
    serverTypeColor: toText(value.serverTypeColor ?? value.typeColor),
    ipCount: toMetric(value.ipCount) ?? ipList.list.length,
    regulationCountryName: toText(value.regulationCountryName ?? value.countryName),
    regulationCountrygFlag: toText(
      value.regulationCountrygFlag ?? value.regulationCountryFlag ?? value.countryFlag ?? value.flag,
    ),
    regulationCountryFlag: toText(value.regulationCountryFlag ?? value.regulationCountrygFlag ?? value.countryFlag),
    leverage: toText(value.leverage),
    leverageDetailList,
    fastestServerIp: toText(value.fastestServerIp ?? value.fastestIp),
    fastestServerPingText: toText(value.fastestServerPingText ?? value.fastestPingText ?? value.pingText),
    fastestServerVelocity: toMetric(value.fastestServerVelocity ?? value.fastestVelocity ?? value.velocity),
    serverIPList: ipList,
    companyBasic: isRecord(value.companyBasic) ? value.companyBasic : null,
  }
}

const normalizeServers = (value: unknown): ExchangeServerItem[] => {
  return parseArray(value)
    .map(normalizeServer)
    .filter((item): item is ExchangeServerItem => Boolean(item))
}

/** 在聚合响应的 data/module 多层结构中定位真正的鉴定对象。 */
const findAppraisePayload = (value: unknown, depth = 0): Record<string, unknown> | null => {
  if (depth > 12 || value === null || value === undefined) return null
  if (Array.isArray(value)) {
    for (const child of value) {
      const found = findAppraisePayload(child, depth + 1)
      if (found) return found
    }
    return null
  }
  if (!isRecord(value)) return null

  if ('identificationResult' in value || 'serverList' in value) return value

  for (const key of ['data', 'Data', 'mt4_mt5_appraise_v2']) {
    const found = findAppraisePayload(value[key], depth + 1)
    if (found) return found
  }

  for (const child of Object.values(value)) {
    const found = findAppraisePayload(child, depth + 1)
    if (found) return found
  }

  return null
}

const resolveCount = (value: Record<string, unknown>, ...keys: string[]) => {
  for (const key of keys) {
    const metric = toMetric(value[key])
    if (metric !== undefined) return metric
  }
  return undefined
}

/** 归一化 MT4/MT5 鉴定结果，包括牌照、服务器及统计字段。 */
export const normalizeExchangeSoftwareAppraise = (response: unknown): ExchangeSoftwareAppraise | null => {
  const payload = findAppraisePayload(unwrapResponse(response))
  if (!payload) return null

  const identificationSource = isRecord(payload.identificationResult) ? payload.identificationResult : {}
  const serverSource = isRecord(payload.serverList) ? payload.serverList : {}
  const licenses = parseArray(identificationSource.licenses ?? identificationSource.licenseList ?? payload.licenses)
    .map(normalizeLicense)
    .filter((item): item is ExchangeSoftwareLicense => Boolean(item))
  const allServers = normalizeServers(serverSource.allServers ?? serverSource.all ?? payload.allServers)
  const mt4Servers = normalizeServers(serverSource.mT4_Servers ?? serverSource.mt4Servers ?? payload.mT4_Servers)
  const mt5Servers = normalizeServers(serverSource.mT5_Servers ?? serverSource.mt5Servers ?? payload.mT5_Servers)

  const normalized: ExchangeSoftwareAppraise = {
    ...payload,
    identificationResult: {
      ...identificationSource,
      licenses,
      mT4_Count: resolveCount(identificationSource, 'mT4_Count', 'MT4_Count', 'mt4Count', 'mT4Count'),
      mT5_Count: resolveCount(identificationSource, 'mT5_Count', 'MT5_Count', 'mt5Count', 'mT5Count'),
      averageVelocityText:
        toText(
          identificationSource.averageVelocityText ??
            identificationSource.averageVelocity ??
            identificationSource.avgVelocity,
        ) || undefined,
      velocityIcon: toText(identificationSource.velocityIcon ?? identificationSource.velocityIconUrl),
    },
    serverList: {
      ...serverSource,
      allServers,
      mT4_Servers: mt4Servers,
      mT5_Servers: mt5Servers,
    },
  }

  if (
    !licenses.length &&
    !allServers.length &&
    !mt4Servers.length &&
    !mt5Servers.length &&
    !normalized.identificationResult?.mT4_Count &&
    !normalized.identificationResult?.mT5_Count
  ) {
    return null
  }

  return normalized
}

/** 解析聚合接口中的 MT4/MT5 软件模块。 */
export const normalizeExchangeLegacySoftwareAggregate = (response: unknown) => {
  const root = unwrapResponse(response)
  const mtModule =
    findFirstByKey(root, 'mt4mt5_trading_software_list_web') ?? findFirstByKey(root, 'mt4mt5_trading_software_list')
  const selfModule = findFirstByKey(root, 'self_developed_software_list')
  const cTraderModule = findFirstByKey(root, 'company_ctrader_software_list')
  const mt4mt5SoftwareList = normalizeSoftwareTabs(mtModule).all
  const selfTabs = normalizeSoftwareTabs(selfModule)
  const cTraderTabs = normalizeSoftwareTabs(cTraderModule)

  return {
    mt4mt5SoftwareList,
    selfSoftwareList: selfTabs.all,
    iosSelfSoftwareList: selfTabs.ios,
    androidSelfSoftwareList: selfTabs.android,
    ctraderSoftwareList: cTraderTabs.all,
    iosCtraderSoftwareList: cTraderTabs.ios,
    androidCtraderSoftwareList: cTraderTabs.android,
  }
}

const normalizeRequestOptions = (options: ExchangeSoftwareRequestOptions = {}) => ({
  ...DEFAULT_REQUEST_OPTIONS,
  ...options,
})

const normalizeTraderCode = (
  value: string | ExchangeSoftwareRequestOptions,
): { traderCode: string; options: ExchangeSoftwareRequestOptions } => {
  if (typeof value === 'string') return { traderCode: value.trim(), options: {} }

  const traderCode = toText(value.traderCode ?? value.code).trim()
  return { traderCode, options: value }
}

const buildSoftwareParams = (traderCode: string, options: ExchangeSoftwareRequestOptions = {}) => {
  const normalized = normalizeRequestOptions(options)
  return {
    traderCode,
    brandType: normalized.brandType,
    isContainerOldApp: normalized.isContainerOldApp,
    deviceType: normalized.deviceType,
  }
}

/** 请求 v4 软件列表，SSR 使用 server fetch，浏览器复用项目 axios 拦截器。 */
const requestSoftwareList = async (url: string, traderCode: string, options: ExchangeSoftwareRequestOptions = {}) => {
  const api = useEnvApi()
  const params = buildSoftwareParams(traderCode, options)
  const config = {
    // 软件 v2 路径目前只在 v4 网关提供，BFF 在部分环境会返回 404。
    baseURL: api.v4,
    url,
    method: 'GET' as const,
    params,
  }

  if (import.meta.server) {
    return await useServerFetch({ ...config, query: params })
  }

  return await request<unknown, unknown>(config)
}

/** 获取交易所自研交易应用（all/iOS/Android）。失败时返回空 tab。 */
export const getExchangeSelfSoftware = async (
  traderCodeOrParams: string | ExchangeSoftwareRequestOptions,
  options: ExchangeSoftwareRequestOptions = {},
) => {
  const normalized = normalizeTraderCode(traderCodeOrParams)
  if (!normalized.traderCode) return { all: [], ios: [], android: [] }

  try {
    const response = await requestSoftwareList(SELF_SOFTWARE_PATH, normalized.traderCode, {
      ...normalized.options,
      ...options,
    })
    return normalizeSoftwareTabs(response)
  } catch {
    return { all: [], ios: [], android: [] }
  }
}

/** 获取交易所 cTrader 应用（all/iOS/Android）。失败时返回空 tab。 */
export const getExchangeCTraderSoftware = async (
  traderCodeOrParams: string | ExchangeSoftwareRequestOptions,
  options: ExchangeSoftwareRequestOptions = {},
) => {
  const normalized = normalizeTraderCode(traderCodeOrParams)
  if (!normalized.traderCode) return { all: [], ios: [], android: [] }

  try {
    const response = await requestSoftwareList(CTRADER_SOFTWARE_PATH, normalized.traderCode, {
      ...normalized.options,
      ...options,
    })
    return normalizeSoftwareTabs(response)
  } catch {
    return { all: [], ios: [], android: [] }
  }
}

/** 调用旧聚合接口，读取 MT4/MT5 软件及旧版本自研/cTrader 兜底列表。 */
export const getExchangeLegacySoftwareAggregate = async (
  traderCodeOrParams: string | ExchangeSoftwareRequestOptions,
  options: ExchangeSoftwareRequestOptions = {},
) => {
  const normalized = normalizeTraderCode(traderCodeOrParams)
  const empty = normalizeExchangeLegacySoftwareAggregate(null)
  if (!normalized.traderCode) return empty

  const mergedOptions = normalizeRequestOptions({ ...normalized.options, ...options })
  const api = useEnvApi()
  const data = {
    ...buildSoftwareParams(normalized.traderCode, mergedOptions),
    tradercode: normalized.traderCode,
    code: normalized.traderCode,
    brokerId: normalized.traderCode,
    languageCode: mergedOptions.languageCode,
    countryCode: mergedOptions.countryCode,
    userId: mergedOptions.userId || '',
  }
  const config = {
    baseURL: import.meta.server ? api.testwikidatacenter : api.homeClient,
    url: `${WIKIBITWEBV1}/invoke-v3/v3`,
    method: 'POST' as const,
    data,
    headers: {
      Routes: SOFTWARE_AGGREGATE_ROUTE,
    },
  }

  try {
    const response = import.meta.server
      ? await useServerFetch(config)
      : await request<unknown, unknown>(config)
    return normalizeExchangeLegacySoftwareAggregate(response)
  } catch {
    return empty
  }
}

/** 获取 MT4/MT5 鉴定和服务器数据；鉴定接口失败不会阻塞详情页。 */
export const getExchangeMt4Mt5Appraise = async (
  traderCodeOrParams: string | ExchangeSoftwareRequestOptions,
  options: ExchangeSoftwareRequestOptions = {},
): Promise<ExchangeSoftwareAppraise | null> => {
  const normalized = normalizeTraderCode(traderCodeOrParams)
  if (!normalized.traderCode) return null

  const mergedOptions = normalizeRequestOptions({ ...normalized.options, ...options })
  const api = useEnvApi()
  const data = {
    ...buildSoftwareParams(normalized.traderCode, mergedOptions),
    tradercode: normalized.traderCode,
    code: normalized.traderCode,
    brokerId: normalized.traderCode,
    type: 1,
    languageCode: mergedOptions.languageCode,
    countryCode: mergedOptions.countryCode,
    userId: mergedOptions.userId || '',
  }
  const config = {
    baseURL: import.meta.server ? api.testwikidatacenter : api.homeClient,
    url: `${WIKIBITWEBV1}/invoke-v3/v3`,
    method: 'POST' as const,
    data,
    headers: {
      Routes: MT4_MT5_APPRAISE_ROUTE,
    },
  }

  try {
    const response = import.meta.server
      ? await useServerFetch(config)
      : await request<unknown, unknown>(config)
    return normalizeExchangeSoftwareAppraise(response)
  } catch {
    return null
  }
}

/** 并行加载软件模块的四类数据；任何单个接口失败都只影响对应子区域。 */
export const getExchangeSoftwareData = async (
  traderCodeOrParams: string | ExchangeSoftwareRequestOptions,
  options: ExchangeSoftwareRequestOptions = {},
): Promise<ExchangeSoftwarePayload> => {
  const normalized = normalizeTraderCode(traderCodeOrParams)
  const empty = emptyExchangeSoftwarePayload()
  if (!normalized.traderCode) return empty

  const mergedOptions = { ...normalized.options, ...options }
  const [selfResult, cTraderResult, legacyResult, appraiseResult] = await Promise.allSettled([
    getExchangeSelfSoftware(normalized.traderCode, mergedOptions),
    getExchangeCTraderSoftware(normalized.traderCode, mergedOptions),
    getExchangeLegacySoftwareAggregate(normalized.traderCode, mergedOptions),
    getExchangeMt4Mt5Appraise(normalized.traderCode, mergedOptions),
  ])

  const self = selfResult.status === 'fulfilled' ? selfResult.value : { all: [], ios: [], android: [] }
  const cTrader = cTraderResult.status === 'fulfilled' ? cTraderResult.value : { all: [], ios: [], android: [] }
  const legacy =
    legacyResult.status === 'fulfilled' ? legacyResult.value : normalizeExchangeLegacySoftwareAggregate(null)
  const appraise = appraiseResult.status === 'fulfilled' ? appraiseResult.value : null

  return {
    mt4mt5SoftwareList: dedupeSoftwareItems(legacy.mt4mt5SoftwareList),
    selfSoftwareList: dedupeSoftwareItems(self.all.length ? self.all : legacy.selfSoftwareList),
    iosSelfSoftwareList: dedupeSoftwareItems(self.ios.length ? self.ios : legacy.iosSelfSoftwareList),
    androidSelfSoftwareList: dedupeSoftwareItems(self.android.length ? self.android : legacy.androidSelfSoftwareList),
    ctraderSoftwareList: dedupeSoftwareItems(cTrader.all.length ? cTrader.all : legacy.ctraderSoftwareList),
    iosCtraderSoftwareList: dedupeSoftwareItems(cTrader.ios.length ? cTrader.ios : legacy.iosCtraderSoftwareList),
    androidCtraderSoftwareList: dedupeSoftwareItems(
      cTrader.android.length ? cTrader.android : legacy.androidCtraderSoftwareList,
    ),
    mt4mt5AppraiseData: appraise,
  }
}

/** 判断软件模块是否有任意可展示内容，供详情页动态注册 Tab。 */
export const hasExchangeSoftwareContent = (payload: ExchangeSoftwarePayload | null | undefined): boolean => {
  if (!payload) return false

  const appraise = payload.mt4mt5AppraiseData
  return Boolean(
    payload.mt4mt5SoftwareList.length ||
    payload.selfSoftwareList.length ||
    payload.iosSelfSoftwareList.length ||
    payload.androidSelfSoftwareList.length ||
    payload.ctraderSoftwareList.length ||
    payload.iosCtraderSoftwareList.length ||
    payload.androidCtraderSoftwareList.length ||
    appraise?.identificationResult?.licenses?.length ||
    appraise?.serverList?.allServers?.length ||
    appraise?.serverList?.mT4_Servers?.length ||
    appraise?.serverList?.mT5_Servers?.length,
  )
}

/** 兼容页面或测试中使用的简短别名。 */
export const getExchangeSoftware = getExchangeSoftwareData
